/**
 * Polish message catalog — mirrors the shape of `messages.en.ts`
 * exactly; `satisfies MessagesShape` makes a missing or renamed key a
 * TypeScript error. Register: ty (informal, locked in l10n/locales.md).
 *
 * Platform terms follow Polish UI conventions (ustawienia, parowanie
 * for pairing, dublowanie for notification mirroring, usuń for dismiss,
 * rozmowa for conversation, schowek for clipboard, odbiornik for a
 * receiving surface). "Glance" is the untranslated feature brand name
 * (glossary D1); „klip” renders the D2 loanword in native script.
 *
 * Plural notes: Polish is a {one, few, many, other} CLDR locale —
 * every count-bearing `selectPlural("pl", …)` supplies all four forms.
 * `one` covers exactly 1; `few` = 2–4, 22–24…; `many` = 0, 5–21…;
 * `other` = fractions. The count is never hardcoded into any form.
 *
 * Out of scope: the same exclusions as the English catalog (log
 * scopes, structured event names, JSDoc, server-emitted error
 * messages — see `messages.en.ts` for the full list).
 */
import { selectPlural } from "@reflecto/client-core/i18n";
import type { MessagesShape } from ".";

export const messages = {
  header: {
    banner: "Reflecto",
    statusConnected: "Połączono",
    statusReconnecting: "Ponowne łączenie…",
    statusDisconnected: "Rozłączono",
    openSettings: "Otwórz ustawienia",
    pairedFallbackDevice: "twój telefon",
    publicModeToggleEnable: "Ukryj treść powiadomień",
    publicModeToggleDisable: "Pokaż treść powiadomień",
  },

  notifications: {
    replyAriaLabel: "Odpowiedz",
    replyPlaceholder: "Odpowiedz…",
    replySend: "Wyślij",
    sent: "Wysłano",
    failedToSend: "Nie udało się wysłać",
    unread: "Nieprzeczytane",
    dismiss: "Usuń",
    notificationImageAlt: "Obraz powiadomienia",
  },

  conversations: {
    replyAriaLabel: "Odpowiedz",
    replyPlaceholder: "Odpowiedz…",
    replySend: "Wyślij",
    sent: "Wysłano",
    failedToSend: "Nie udało się wysłać",
    dismiss: "Usuń rozmowę",
    messagesListLabel: "Wiadomości w tej rozmowie",
    deviceOwner: "Ty",
    unreadAriaLabel: (count: number): string =>
      count === 0
        ? "Brak nieprzeczytanych wiadomości"
        : `${count} ${selectPlural("pl", count, {
            one: "nieprzeczytana wiadomość",
            few: "nieprzeczytane wiadomości",
            many: "nieprzeczytanych wiadomości",
            other: "nieprzeczytanej wiadomości",
          })}`,
  },

  list: {
    today: "Dziś",
    yesterday: "Wczoraj",
    searchNotifications: "Szukaj powiadomień",
    clearSearch: "Wyczyść wyszukiwanie",
    clearAll: "Usuń wszystkie",
    markAllRead: "Oznacz wszystkie jako przeczytane",
    groupCheckboxLabel: "Grupuj",
    noNotificationsTitle: "Brak powiadomień",
    noNotificationsDesc: "Nowe wiadomości zdublowane z twojego telefonu pojawią się tutaj.",
    noMatchesTitle: "Brak wyników",
    notificationsList: "Powiadomienia",
    privacyAppList: "Powiadomienia według aplikacji",
    clear: "Usuń",
    clearMatches: (count: number): string =>
      `Usuń ${count} ${selectPlural("pl", count, {
        one: "dopasowanie",
        few: "dopasowania",
        many: "dopasowań",
        other: "dopasowania",
      })}`,
    noMatchesDesc: (query: string): string => `Brak powiadomień pasujących do „${query}”.`,
    appNotificationsGroup: (appName: string): string => `Powiadomienia ${appName}`,
    dismissAllFrom: (appName: string): string => `Usuń wszystkie z ${appName}`,
    privacyCount: (count: number, kind: "messages" | "calls" | "notifications") => {
      const noun =
        kind === "messages"
          ? selectPlural("pl", count, {
              one: "wiadomość",
              few: "wiadomości",
              many: "wiadomości",
              other: "wiadomości",
            })
          : kind === "calls"
            ? selectPlural("pl", count, {
                one: "połączenie",
                few: "połączenia",
                many: "połączeń",
                other: "połączenia",
              })
            : selectPlural("pl", count, {
                one: "powiadomienie",
                few: "powiadomienia",
                many: "powiadomień",
                other: "powiadomienia",
              });
      return `${count} ${noun}`;
    },
    kbdDismiss: "usuń",
    kbdNavigate: "nawigacja",
    kbdOrSeparator: "lub",
  },

  settings: {
    back: "Powrót do powiadomień",
    settings: "Ustawienia",
    general: "Ogólne",
    theme: "Motyw",
    themeAuto: "Automatyczny",
    themeLight: "Jasny",
    themeDark: "Ciemny",
    language: "Język",
    languageSystemDefault: "Jak w systemie",
    notificationsSection: "Powiadomienia",
    osNotifications: "Powiadomienia systemowe",
    callNotifications: "Powiadomienia o połączeniach",
    privacySecurity: "Prywatność",
    analyticsToggle: "Anonimowe statystyki użytkowania",
    analyticsToggleDescription: "Tylko liczby i kamienie milowe — nigdy twoje powiadomienia.",
    statusConnected: "Połączono",
    statusReconnecting: "Ponowne łączenie…",
    statusDisconnected: "Rozłączono",
    disconnectFromPhone: "Odłącz od telefonu",
    disconnectConfirm: "Na pewno? Spowoduje to anulowanie parowania twojego urządzenia.",
    yesDisconnect: "Tak, odłącz",
    cancel: "Anuluj",
    version: "Wersja",
    encryption: "Szyfrowanie",
    encryptionValue: "E2E (NaCl)",
    needHelp: "Potrzebujesz pomocy? Napisz na __EMAIL__",
    disconnectFailed: "Nie udało się rozłączyć — spróbuj ponownie",
    disconnectLocalCleanupFailedWithError: (errorMsg: string): string =>
      `Rozłączono, ale nie udało się wyczyścić danych lokalnych: ${errorMsg}. Odśwież stronę.`,
    disconnectLocalCleanupFailed:
      "Rozłączono, ale nie udało się wyczyścić danych lokalnych. Odśwież stronę.",
  },

  pairing: {
    enterCodeInstruction: "Wprowadź ten kod w aplikacji Reflecto",
    pairedSuccessfully: "Sparowano pomyślnie!",
    pairingFailed: "Parowanie nie powiodło się",
    requestingPairingCode: "Uzyskiwanie kodu parowania…",
    codeExpired: "Kod wygasł",
    tryAgain: "Spróbuj ponownie",
    failedToStartPairing: "Nie udało się rozpocząć parowania",
    installHeading: "Nie masz jeszcze aplikacji?",
    installScanHint: "Zeskanuj aparatem telefonu",
    installQrLabel: "Kod QR do zainstalowania Reflecto na telefonie",
    capabilitiesHeading: "Po sparowaniu możesz:",
    capabilityMirror: "Widzieć tutaj powiadomienia z telefonu",
    capabilityReply: "Odpowiadać bez sięgania po telefon",
    capabilityShare: "Udostępniać schowek i pliki w obie strony",
    encryptionNote: "Szyfrowanie end-to-end. Serwer nigdy nie widzi twoich treści.",
  },

  dnd: {
    title: "Nie przeszkadzać",
    description: "Wstrzymaj powiadomienia na tym urządzeniu na najbliższą godzinę.",
    snooze1h: "Wstrzymaj na 1 godzinę",
    snoozeActiveUntil: (formattedTime: string): string => `Aktywne do ${formattedTime}`,
    cancelSnooze: "Anuluj",
  },

  connectors: {
    title: "Połączone usługi",
    emptyBody:
      "Nie masz jeszcze połączonych usług. Połącz ChatGPT lub Claude w ustawieniach ich konektorów, a następnie zatwierdź kod na telefonie.",
    scopeSend: "Może wysyłać powiadomienia",
    added: (date: string): string => `Dodano ${date}`,
    lastUsed: (relative: string): string => `Ostatnio użyto ${relative}`,
    neverUsed: "Nigdy nie użyto",
    revoke: "Odłącz",
    revokeConfirm: (label: string): string => `Odłączyć ${label}? Natychmiast utraci dostęp.`,
    revokeAction: "Odłącz",
    revokeConfirmLabel: (label: string): string => `Odłącz ${label}`,
    cancel: "Anuluj",
    loadError: "Nie udało się załadować połączonych usług. Spróbuj ponownie.",
    revokeError: "Nie udało się odłączyć usługi. Spróbuj ponownie.",
    retry: "Ponów",
  },

  safetyNumber: {
    rowLabel: "Weryfikuj szyfrowanie",
    rowSublabel: "Porównaj numery bezpieczeństwa z telefonem",
    cardTitle: "Numer bezpieczeństwa",
    caption: (deviceLabel: string): string =>
      `Porównaj z numerem wyświetlonym na ${deviceLabel}. Jeśli oba się zgadzają, twoje szyfrowanie end-to-end jest nienaruszone.`,
    repairNote: "Ten numer zmienia się po sparowaniu urządzenia.",
    copy: "Kopiuj",
    copied: "Skopiowano",
    maskedHint: "Ukryte, gdy tryb prywatności jest włączony",
  },

  feedback: {
    title: "Wyślij opinię",
    close: "Zamknij",
    messageLabel: "Twoja wiadomość",
    messagePlaceholder: "Widzisz błąd, masz pomysł lub coś jest nie tak? Napisz do nas.",
    categoryLabel: "Kategoria",
    categoryBug: "Błąd",
    categoryIdea: "Pomysł",
    categoryOther: "Inne",
    emailLabel: "E-mail (opcjonalnie, tylko jeśli chcesz otrzymać odpowiedź)",
    emailPlaceholder: "you@example.com",
    emailHelp: "Nie dodamy cię do żadnej listy.",
    disclosure:
      "Twoja wiadomość jest wysyłana na serwer Reflecto i zapisywana w naszym wewnętrznym systemie zgłoszeń. Dołączamy wersję aplikacji oraz identyfikator urządzenia przypisany podczas parowania, aby powiązać zgłoszenia. Nie widzimy twoich powiadomień, kontaktów ani innych danych osobowych. E-mail jest opcjonalny — podaj go tylko, jeśli chcesz otrzymać odpowiedź. Nie dodamy cię do żadnej listy.",
    submit: "Wyślij opinię",
    sending: "Wysyłanie…",
    success: "Dziękujemy — przeczyta to człowiek, zwykle w ciągu 24 godzin",
    error: "Nie udało się wysłać. Spróbuj ponownie.",
    done: "Gotowe",
    sendFeedback: "Wyślij opinię",
  },

  uninstall: {
    headline: "Rozszerzenie Reflecto zostało odinstalowane",
    body: "Konta nigdy nie było, a rozszerzenie trzymało twoje parowanie na tym komputerze, więc nie zostało nic do posprzątania.",
    ask: "Jeśli masz trzydzieści sekund, chcielibyśmy wiedzieć, co poszło nie tak.",
    categoryLegend: "Jakiego rodzaju to opinia?",
    categoryBug: "Coś się zepsuło",
    categoryIdea: "Brakowało czegoś, czego potrzebuję",
    categoryOther: "Coś innego",
    messageLabel: "Co poszło nie tak?",
    messagePlaceholder: "Po kilku godzinach powiadomienia przestały przychodzić.",
    messageRequired: "Najpierw napisz zdanie, potem wyślij.",
    charactersLeft: (count: number): string => `Pozostało ${count} znaków`,
    emailLabel: "E-mail (opcjonalnie)",
    emailPlaceholder: "you@example.com",
    emailHelp: "Używamy go tylko po to, żeby ci odpowiedzieć. Do niczego więcej.",
    submit: "Wyślij opinię",
    sending: "Wysyłanie…",
    retry: "Spróbuj ponownie",
    privacyNote:
      "Ten formularz nie jest powiązany z żadnym urządzeniem. Nie zawiera twoich powiadomień, schowka ani plików. E-mail jest opcjonalny i służy wyłącznie do odpowiedzi.",
    success: "Dziękujemy. Każdą z tych wiadomości czyta człowiek.",
    errorGeneric: "Nie udało się wysłać. Twoja wiadomość nadal tu jest.",
    errorRateLimited:
      "W ostatnim czasie wysłano stąd kilka wiadomości. Spróbuj ponownie za mniej więcej godzinę. Twój tekst tu zostanie.",
    footerWebApp: "Aplikacja internetowa Reflecto działa też bez rozszerzenia.",
    footerWebAppLink: "Otwórz aplikację internetową",
  },

  glance: {
    title: "Glance",
    appName: "Reflecto",
    closeLabel: "Zamknij",
    aiSummaryTag: "Podsumowanie AI",
    errorAi: "Nie udało się teraz utworzyć podsumowania.",
    retryCta: "Spróbuj ponownie",
    downloadHeading: "Podsumowania AI",
    downloadBody:
      "Nadrób zaległości dzięki podsumowaniu AI tworzonym na urządzeniu. Wszystko działa w całości na twoim urządzeniu.",
    downloadCta: "Pobierz model",
    downloadPrivacyNote: "Jednorazowe pobranie. Serwer nigdy nie widzi twoich powiadomień.",
    downloadingHeading: "Pobieranie modelu…",
    caughtUp: "Wszystko na bieżąco — nic teraz nie wymaga twojej uwagi.",
    needsYou: "Wymaga uwagi",
    quiet: "Pozostałe",
    scanning: (pending: number): string => `Podsumowuję ${pending}…`,
    jump: "Otwórz powiadomienie",
    affordanceReply: "Odpowiedz",
    affordanceOpen: "Otwórz",
    affordanceCopyOtp: "Kopiuj kod",
    buttonLabel: "Glance",
  },

  update: {
    title: "Dostępna aktualizacja",
    description: "Nowa wersja Reflecto jest gotowa. Odśwież stronę, aby zaktualizować.",
    reload: "Odśwież",
  },

  pairConfirm: {
    cardMessage: (name: string): string => `Nazwaliśmy to urządzenie „${name}”.`,
    cardLooksGood: "Pasuje",
    cardEdit: "Edytuj",
    cardAriaLabel: "Potwierdź nazwę urządzenia",
    renameTitle: "Zmień nazwę tego urządzenia",
    renameLabel: "Nazwa urządzenia",
    renameSave: "Zapisz",
    renameCancel: "Anuluj",
    renameFailed: "Nie udało się zmienić nazwy. Spróbuj ponownie.",
  },

  collision: {
    title: (name: string): string => `Masz już „${name}”`,
    subtitle: "Jak nazwiemy to urządzenie?",
    chipWork: (name: string): string => `Służbowy ${name}`,
    chipHome: (name: string): string => `Domowy ${name}`,
    chipPersonal: (name: string): string => `Osobisty ${name}`,
    customLabel: "Własna nazwa",
    save: "Zapisz",
    useAnyway: "Użyj oryginalnej nazwy mimo to",
    saveFailed: "Nie udało się zapisać nowej nazwy. Spróbuj ponownie.",
    ariaLabel: "Rozwiąż konflikt nazw urządzeń",
  },

  clipboard: {
    title: "Schowek",
    emptyTitle: "Brak klipów",
    empty: "Klipy z innych twoich urządzeń pojawiają się tutaj. Naciśnij, aby skopiować.",
    from: (label: string): string => `od ${label}`,
    to: (label: string): string => `do ${label}`,
    copy: "Kopiuj do schowka",
    copied: "Skopiowano",
    reveal: "Pokaż",
    hide: "Ukryj",
    maskedHint: "Treść ukryta — naciśnij „Pokaż”, aby wyświetlić",
    sensitiveChip: "Poufne",
    cloudChip: "przez twój token API",
    delete: "Usuń klip",
    sendPlaceholder: "Wklej tekst do wysłania…",
    sendCta: "Wyślij",
    sendSensitiveToggle: "Oznacz jako poufne",
    sendSuccess: (n: number): string =>
      `Wysłano na ${n} ${selectPlural("pl", n, {
        one: "urządzenie",
        few: "urządzenia",
        many: "urządzeń",
        other: "urządzenia",
      })}.`,
    sendPartial: (n: number, total: number): string =>
      `Dotarło do ${n}/${total} ${selectPlural("pl", total, {
        one: "urządzenia",
        few: "urządzeń",
        many: "urządzeń",
        other: "urządzenia",
      })}.`,
    sendNoDevices: "Brak sparowanych urządzeń do wysłania.",
    sendFailed: "Nie udało się wysłać. Spróbuj ponownie.",
    sendEmpty: "Nie ma nic do wysłania.",
    dockRecentHeading: "Ostatnie",
    dockEmptyClipboard: "Twój schowek jest pusty.",
    clearAll: "Usuń wszystkie",
    clearAllConfirm: "Tak, usuń wszystkie",
    clearAllCancel: "Anuluj",
    shareTitle: "Wyślij na swoje urządzenia",
    shareReview:
      "Sprawdź, co wysyłasz. Trafi to na twoje sparowane urządzenia z szyfrowaniem end-to-end.",
    shareSend: "Wyślij",
    shareCancel: "Anuluj",
    shareNotPaired: "Aby wysłać udostępniony tekst, najpierw sparuj urządzenie.",
    shareOpenApp: "Otwórz Reflecto",
    pasteFromClipboard: "Wklej ze schowka",
    readFailed: "Nie udało się odczytać schowka — wklej poniżej.",
  },
  install: {
    title: "Zainstaluj Reflecto",
    body: "Dodaj do ekranu głównego, aby uzyskać szybszy dostęp.",
    cta: "Zainstaluj",
    dismissAria: "Zamknij monit o instalacji",
  },
  nav: {
    home: "Główna",
    clipboard: "Schowek",
    files: "Pliki",
    ariaLabel: "Nawigacja główna",
    fileHiddenPublicMode: "Ukryte, gdy tryb prywatny jest włączony.",
  },
  common: {
    loading: "Ładowanie",
  },
  errors: {
    notPaired: "Nie sparowano",
  },
  device: {
    nameTemplate: (browser: string, os: string) => `${browser} na ${os}`,
    webFallback: "Przeglądarka WWW",
  },

  palette: {
    placeholder: "Wpisz polecenie…",
    noResults: "Brak pasujących poleceń",
    ariaLabel: "Paleta poleceń",
    clearAll: "Usuń wszystkie powiadomienia",
    glance: "Podsumowanie Glance",
    cancelSnooze: "Anuluj wstrzymanie",
    copyClip: "Kopiuj ostatni odebrany klip",
  },

  fileTransfer: {
    sendTitle: "Wyślij plik",
    sendChooseFile: "Wybierz plik",
    sendDeviceLabel: "Wyślij do",
    sendPhoneFallbackName: "Telefon",
    send: "Wyślij plik",
    sendPreparing: (pct: number | null): string =>
      pct === null ? "Przygotowywanie…" : `Przygotowywanie — ${pct}%`,
    sendDelivering: "Dostarczanie do telefonu…",
    sendWaitingAccept: "Oczekiwanie na akceptację telefonu…",
    sendWaitingAppOpen: "Otwórz Reflecto na telefonie, aby zaakceptować",
    sendWaitingAppOpenHint:
      "Telefon nie może odbierać powiadomień push, więc pokaże ten plik dopiero po otwarciu Reflecto.",
    sendUploading: (partNumber: number, partCount: number): string =>
      `Przesyłanie części ${partNumber} z ${partCount}…`,
    sendDone: "Wysłano.",
    sendFailedRejected: "Twój telefon odrzucił plik.",
    sendFailedNoAnswer: "Brak odpowiedzi z telefonu.",
    sendFailedUploadFailed: "Przesyłanie nie powiodło się. Spróbuj ponownie.",
    sendFailedCancelled: "Anulowano.",
    sendFailedGeneric: "Coś poszło nie tak. Spróbuj ponownie.",
    sendTooLarge: (maxMiB: number): string => `Plik jest za duży (maks. ${maxMiB} MiB).`,
    sendKeepOpenHint: "Nie zamykaj tej karty, dopóki transfer się nie zakończy.",
    sendAnother: "Wyślij kolejny",
    sendNoDevice: "Aby wysyłać pliki, najpierw sparuj telefon.",
    sendCancel: "Anuluj",
    sendPeerProgress: (chunksDone: number): string =>
      `Fragmenty odebrane przez telefon: ${chunksDone}.`,

    receiveTitle: "Pliki przychodzące",
    receiveOfferFrom: (sender: string): string => `${sender} chce wysłać ci plik`,
    receiveAccept: "Akceptuj",
    receiveDecline: "Odrzuć",
    receiveReceiving: (n: number, total: number): string => `Odbieranie części ${n} z ${total}…`,
    receiveDone: "Zapisano.",
    receiveFailed: "Transfer nie powiódł się.",
    receiveCancelled: "Anulowano.",
    receiveCancel: "Anuluj",

    errorChunkAuthFail:
      "Transfer nie powiódł się — nie udało się zweryfikować danych. Spróbuj ponownie.",
    errorHashMismatch:
      "Transfer nie powiódł się — plik nie przeszedł kontroli integralności. Spróbuj ponownie.",
    errorStorageFull: "Za mało miejsca na ten transfer.",
    errorExpired: "Transfer wygasł.",
    errorQuotaExceeded: "Osiągnięto dzienny limit transferów — spróbuj ponownie jutro.",
    errorInternal: "Transfer nie powiódł się — coś poszło nie tak.",
    /** Status while bytes move. Derived from completed ~5 MiB parts, so it steps coarsely. */
    transferringPct: (pct: number): string => `${pct}%`,
    /** aria-label for the x on a FINISHED row — clears it, does not stop anything. */
    dismiss: "Zamknij",
    /** aria-label for the progress bar; a bare percentage announces nothing useful alone. */
    progressLabel: "Postęp przesyłania",
    outgoing: (n: number): string => `Wychodzące (${n})`,
    incoming: (n: number): string => `Przychodzące (${n})`,
    /** Bulk action; only shown when more than one offer is still pending. */
    acceptAll: "Akceptuj wszystkie",
    dropTitle: "Przeciągnij i upuść tutaj",
    /** `size` is pre-formatted and derived from the protocol ceiling at runtime — never
     *  hardcode a number here, the server can tighten the cap. */
    dropLimit: (size: string): string => `Pliki do ${size}.`,
    /** Must contain dropTitle verbatim: WCAG 2.5.3 (Label in Name). */
    dropAriaLabel: "Przeciągnij i upuść tutaj lub wybierz plik do wysłania",
    /** Chip on a transfer row: bytes travel device-to-device, not via the relay. */
    transportDirect: "Bezpośrednio",
    transportDirectHint:
      "Wysłane między urządzeniami — nigdy nie przechowywane na naszych serwerach.",
    /** Chip for the relay route. BOTH routes are end-to-end encrypted — this is a
     *  route/speed distinction, never a security one, so the copy must not alarm. */
    transportRelay: "Przez serwer",
    transportRelayHint:
      "Przekazane przez nasze serwery, nadal szyfrowane end-to-end — nie możemy tego odczytać.",
    directBadge: "Bezpośrednio",
  },
  time: {
    justNow: "przed chwilą",
    minutesAgo: (count: number): string => `${count} min temu`,
    hoursAgo: (count: number): string => `${count} godz. temu`,
  },
} satisfies MessagesShape;
